import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, UtensilsCrossed, BadgeDollarSign } from "lucide-react";
import { api, formatDate } from "@/lib/api";

export default function Rebook() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | done | refunded | error
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState(null); // {date, seating}
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get(`/rebook/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.final_status === "rebooked") setState("done");
        else if (r.data.final_status === "refund_requested") setState("refunded");
        else setState("ready");
      })
      .catch(() => setState("error"));
  }, [token]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/rebook/${token}`, { ...selected, action: "reschedule" });
      setConfirmed(r.data);
      setState("done");
    } catch (e) {
      alert(e.response?.data?.detail || "Could not rebook. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const requestRefund = async () => {
    if (!window.confirm("Request a full refund for your dinner reservation? Our team will process it and email you.")) return;
    setSubmitting(true);
    try {
      await api.post(`/rebook/${token}`, { action: "refund" });
      setState("refunded");
    } catch (e) {
      alert(e.response?.data?.detail || "Could not submit your refund request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-6 py-16" data-testid="rebook-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <p className="font-display font-extrabold text-xl uppercase tracking-tighter">Immersion Festival</p>
          <p className="text-[11px] uppercase tracking-[0.35em] text-gray-500 mt-2">Iceland · 1–5 October 2026</p>
        </div>

        <div className="border border-white/10 bg-[#0C0C0E] p-8">
          {state === "loading" && (
            <div className="text-center py-10 text-gray-500"><Loader2 className="animate-spin mx-auto mb-4" /> Loading…</div>
          )}

          {state === "error" && (
            <div className="text-center py-6" data-testid="rebook-error">
              <XCircle className="text-[#e07a6c] mx-auto mb-4" size={40} />
              <p className="text-white font-bold mb-2">Link not found</p>
              <p className="text-gray-500 text-sm">This rebooking link is no longer valid. Please contact us at catering@privatechef.is and we'll help.</p>
            </div>
          )}

          {state === "ready" && info && (
            <div>
              <div className="text-center mb-6">
                <UtensilsCrossed className="text-[#c9a24b] mx-auto mb-3" size={34} />
                <p className="text-xs uppercase tracking-[0.2em] text-[#c9a24b] mb-2">Choose Your New Evening</p>
                <h1 className="font-display font-extrabold text-2xl">Your dinner has moved</h1>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 text-center">
                Your dinner on <span className="text-white font-semibold">{formatDate(info.booking.current_date)}</span> won't take place. Please pick a new time that is available below for your party of <span className="text-white font-semibold">{info.booking.party_size}</span>.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-7" data-testid="rebook-options">
                {info.options.map((o) => {
                  const isSel = selected && selected.date === o.date && selected.seating === o.seating;
                  return (
                    <button
                      key={`${o.date}-${o.seating}`}
                      data-testid={`rebook-option-${o.date}-${o.seating}`}
                      disabled={!o.available}
                      onClick={() => setSelected({ date: o.date, seating: o.seating })}
                      className={`py-5 px-4 border text-center transition-all disabled:opacity-30 disabled:cursor-not-allowed ${isSel ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}
                    >
                      <span className="block text-[11px] uppercase tracking-[0.15em] opacity-70">{formatDate(o.date)}</span>
                      <span className="block font-display font-bold text-2xl mt-1">{o.seating}</span>
                      <span className="block text-[10px] uppercase tracking-[0.12em] mt-2 opacity-70">{o.available ? "Available" : "Sold out"}</span>
                    </button>
                  );
                })}
              </div>

              <button
                data-testid="rebook-confirm"
                onClick={submit}
                disabled={!selected || submitting}
                className="w-full bg-[#c9a24b] hover:bg-[#d9b45e] text-black uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={15} />} Confirm new time
              </button>

              {info.allow_refund && (
                <div className="mt-5 pt-5 border-t border-white/10 text-center">
                  <p className="text-gray-500 text-xs mb-3">None of the available times work for you?</p>
                  <button
                    data-testid="rebook-refund"
                    onClick={requestRefund}
                    disabled={submitting}
                    className="w-full border border-white/20 text-gray-300 hover:border-white/50 hover:text-white uppercase tracking-[0.15em] text-xs font-bold py-3.5 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40"
                  >
                    <BadgeDollarSign size={15} /> Request a full refund instead
                  </button>
                </div>
              )}
            </div>
          )}

          {state === "done" && (
            <div className="text-center py-6" data-testid="rebook-done">
              <CheckCircle2 className="text-emerald-400 mx-auto mb-4" size={44} />
              <p className="text-white font-bold text-lg mb-2">You're all set — thank you!</p>
              <p className="text-gray-400 text-sm">
                {confirmed
                  ? <>Your dinner is now booked for <span className="text-white font-semibold">{formatDate(confirmed.date)} at {confirmed.seating}</span>. We've emailed you an updated confirmation with a new QR pass.</>
                  : <>Your dinner has already been rebooked. Check your email for the updated confirmation and QR pass.</>}
              </p>
            </div>
          )}

          {state === "refunded" && (
            <div className="text-center py-6" data-testid="rebook-refunded">
              <BadgeDollarSign className="text-[#c9a24b] mx-auto mb-4" size={44} />
              <p className="text-white font-bold text-lg mb-2">Refund request received</p>
              <p className="text-gray-400 text-sm">We've received your request for a full refund. Our team will process it and be in touch shortly. We're sorry we couldn't host you this time.</p>
            </div>
          )}
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">Questions? Email catering@privatechef.is and we'll help.</p>
      </div>
    </div>
  );
}
